import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Auth0ActivationStatus } from '../models/auth0-activation-status.model';

/** Result of activating or deactivating an Auth0 user. */
export interface Auth0ActiveResult {
  personId: string;
  active: boolean;
}

/**
 * Profile fields to change on an Auth0 user. Only the fields being changed are sent; the phone
 * number is E.164, e.g. +14145550100.
 */
/** Result of requesting a password reset email for an Auth0 user. */
export interface Auth0PasswordResetResult {
  personId: string;
  sent: boolean;
}

/** Result of clearing an Auth0 user's brute-force lockout. */
export interface Auth0UnlockResult {
  personId: string;
  unlocked: boolean;
}

export interface Auth0ProfileChanges {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

/** The Auth0 user's profile as it stands after an update. */
export interface Auth0Profile {
  personId: string;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
}

/**
 * Admin identity operations served by the JXP API (jeweler-platform-backend), which
 * holds the Unified Identity API credential so this app never does.
 */
@Injectable({
  providedIn: 'root'
})
export class AdminIdentityService {
  private readonly apiDomain = environment.jxpApiUrl;

  constructor(private http: HttpClient) {}

  /**
   * False in environments with no paired JXP API, where the status cannot be read at all.
   */
  get isAvailable(): boolean {
    return !!this.apiDomain;
  }

  /**
   * Reads whether an Auth0-resident person has activated their account. The JXP API reads the
   * person from membership as the signed-in representative, so it refuses a person this
   * representative could not already see.
   *
   * @param personId Membership person id.
   */
  getActivationStatus(personId: string): Observable<Auth0ActivationStatus> {
    return this.http
      .get<{ data: Auth0ActivationStatus }>(`${this.personUrl(personId)}/activation-status`)
      .pipe(map(response => response.data));
  }

  /**
   * Activates or deactivates (blocks) the person's Auth0 user. This is the account's own
   * active flag; it does not clear an Auth0 attack-protection block.
   *
   * @param personId Membership person id.
   * @param active True to activate, false to deactivate.
   */
  setActive(personId: string, active: boolean): Observable<Auth0ActiveResult> {
    return this.http
      .post<{ data: Auth0ActiveResult }>(`${this.personUrl(personId)}/${active ? 'activate' : 'deactivate'}`, {})
      .pipe(map(response => response.data));
  }

  /**
   * Changes the name and/or phone number on the person's Auth0 user. Email is not offered: the
   * Unified Identity API cannot change it.
   *
   * @param personId Membership person id.
   * @param changes Only the fields that changed; at least one.
   */
  updateProfile(personId: string, changes: Auth0ProfileChanges): Observable<Auth0Profile> {
    return this.http
      .patch<{ data: Auth0Profile }>(`${this.personUrl(personId)}/profile`, changes)
      .pipe(map(response => response.data));
  }

  /**
   * Clears every Auth0 brute-force block on the person, from any IP address. Does not reactivate
   * a deactivated account; that is setActive.
   *
   * @param personId Membership person id.
   */
  unlock(personId: string): Observable<Auth0UnlockResult> {
    return this.http
      .post<{ data: Auth0UnlockResult }>(`${this.personUrl(personId)}/unlock`, {})
      .pipe(map(response => response.data));
  }

  /**
   * Asks Auth0 to email the person a password reset. Auth0 sends it with its own template and
   * picks the wording (an invitation for a user who was never activated), so no link or expiry
   * comes back here.
   *
   * @param personId Membership person id.
   */
  sendPasswordReset(personId: string): Observable<Auth0PasswordResetResult> {
    return this.http
      .post<{ data: Auth0PasswordResetResult }>(`${this.personUrl(personId)}/password-reset`, {})
      .pipe(map(response => response.data));
  }

  private personUrl(personId: string): string {
    return `${this.apiDomain}/v1/admin/identity/persons/${encodeURIComponent(personId)}`;
  }
}
